import { Button } from './ui/button';
import { SidebarTrigger } from './ui/sidebar';
import { HelpCircle, Menu, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useState, useEffect, useRef } from 'react';

interface NavbarProps {
  onHelpClick?: () => void;
}

export default function Navbar({ onHelpClick }: NavbarProps) {
  const { language, setLanguage } = useLanguage();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { id: 'english', name: 'English', flag: '🇺🇸' },
    { id: 'arabic', name: 'العربية', flag: '🇸🇦' },
    { id: 'kurdish', name: 'کوردی', flag: '🇮🇶' }
  ] as const;

  const currentLanguage = languages.find(lang => lang.id === language);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLanguageOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 px-6 py-4 relative z-50">
      <div className="flex items-center justify-between">
        {/* Left side - Sidebar toggle and page title */}
        <div className="flex items-center space-x-4">
          <SidebarTrigger className="h-8 w-8 p-0">
            <Menu className="h-4 w-4" />
            <span className="sr-only">Toggle Sidebar</span>
          </SidebarTrigger>
          <h2 className="text-lg font-semibold text-gray-800">Dashboard</h2>
        </div>

        {/* Right side - Language selector and Help button */}
        <div className="flex items-center space-x-4">
          {/* Language Selector */}
          <div className="relative" ref={dropdownRef}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
              className="flex items-center space-x-2 hover:bg-gray-50"
            >
              <Globe className="h-4 w-4" />
              <span>{currentLanguage?.flag}</span>
            </Button>
            
            {isLanguageOpen && (
              <div className="fixed right-6 top-20 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999]">
                <div className="py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => {
                        setLanguage(lang.id);
                        setIsLanguageOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 ${
                        language === lang.id ? 'bg-cyan-50 text-cyan-700' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Help button */}
          <Button
            variant="outline"
            size="sm"
            onClick={onHelpClick}
            className="flex items-center space-x-2 hover:bg-gray-50"
          >
            <HelpCircle className="h-4 w-4" />
            <span>Help</span>
          </Button>
        </div>
      </div>
    </nav>
  );
}
